/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import type { KeyboardEventHandler, RefObject } from "react"
import { useCallback, useEffect, useRef } from "react"
import { useCollator } from "react-aria"
import type { Key } from "react-aria-components"

// Adapted from https://github.com/adobe/react-spectrum/blob/main/packages/@react-aria/selection/src/useTypeSelect.ts
const TYPEAHEAD_DEBOUNCE_WAIT_MS = 1000
const MENU_TEXT_VALUE_ATTRIBUTE = "data-menu-text-value"

export function isTextEntryTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || target.closest("input, textarea, select") !== null)
  )
}

interface KeyboardDelegate<T extends Key> {
  getKeyForSearch: (search: string, fromKey?: T | null) => T | null
}

interface SelectionManager<T extends Key> {
  readonly focusedKey: T | null
  setFocusedKey: (key: T) => void
}

interface TypeSelectOptions<T extends Key> {
  keyboardDelegate: KeyboardDelegate<T>
  selectionManager: SelectionManager<T>
  onTypeSelect?: (key: T) => void
}

interface TypeSelectState {
  search: string
  timeout: ReturnType<typeof setTimeout> | undefined
}

interface TypeSelectAria {
  typeSelectProps: {
    onKeyDownCapture: KeyboardEventHandler<HTMLElement>
    onKeyDown: KeyboardEventHandler<HTMLElement>
  }
  resetTypeSelect: () => void
}

function getMenuItems(root: HTMLElement | null): HTMLElement[] {
  if (!root) return []
  return [...root.querySelectorAll<HTMLElement>(`[${MENU_TEXT_VALUE_ATTRIBUTE}]`)].filter(
    (item) =>
      item.closest('[aria-hidden="true"]') === null &&
      !item.matches(":disabled, [aria-disabled='true']") &&
      item.getClientRects().length > 0,
  )
}

export function useMenuTypeSelect(rootRef: RefObject<HTMLElement | null>): TypeSelectAria {
  const collator = useCollator({ usage: "search", sensitivity: "base" })

  return useTypeSelect<number>({
    keyboardDelegate: {
      getKeyForSearch(search, fromKey) {
        const items = getMenuItems(rootRef.current)
        const startIndex = fromKey ?? 0
        for (let index = startIndex; index < items.length; index += 1) {
          const text = items[index]?.getAttribute(MENU_TEXT_VALUE_ATTRIBUTE) ?? ""
          if (text && collator.compare(text.slice(0, search.length), search) === 0) return index
        }
        return null
      },
    },
    selectionManager: {
      get focusedKey() {
        const items = getMenuItems(rootRef.current)
        const focusedIndex = items.findIndex(
          (item) => item === rootRef.current?.ownerDocument.activeElement,
        )
        return focusedIndex === -1 ? null : focusedIndex
      },
      setFocusedKey(key) {
        getMenuItems(rootRef.current)[key]?.focus({ preventScroll: true })
      },
    },
  })
}

function useTypeSelect<T extends Key>({
  keyboardDelegate,
  selectionManager,
  onTypeSelect,
}: TypeSelectOptions<T>): TypeSelectAria {
  const state = useRef<TypeSelectState>({ search: "", timeout: undefined }).current
  const resetTypeSelect = useCallback(() => {
    state.search = ""
    if (state.timeout !== undefined) clearTimeout(state.timeout)
    state.timeout = undefined
  }, [state])

  useEffect(
    () => () => {
      resetTypeSelect()
    },
    [resetTypeSelect],
  )

  const search = (character: string): T | null => {
    state.search += character
    let key = keyboardDelegate.getKeyForSearch(state.search, selectionManager.focusedKey)
    if (key === null) key = keyboardDelegate.getKeyForSearch(state.search)
    if (key === null) {
      resetTypeSelect()
      return null
    }

    selectionManager.setFocusedKey(key)
    onTypeSelect?.(key)
    if (state.timeout !== undefined) clearTimeout(state.timeout)
    state.timeout = setTimeout(resetTypeSelect, TYPEAHEAD_DEBOUNCE_WAIT_MS)
    return key
  }

  const onKeyDownCapture: KeyboardEventHandler<HTMLElement> = (event) => {
    if (state.search.length === 0 || event.key !== " " || isTextEntryTarget(event.target)) return

    event.preventDefault()
    if (!("continuePropagation" in event) || !event.isPropagationStopped()) {
      event.stopPropagation()
    }
    search(" ")
  }

  const onKeyDown: KeyboardEventHandler<HTMLElement> = (event) => {
    const character = getStringForKey(event.key)
    const isWithinCurrentTarget = event.nativeEvent.composedPath().includes(event.currentTarget)
    if (
      !character ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      !isWithinCurrentTarget ||
      isTextEntryTarget(event.target) ||
      (state.search.length === 0 && character === " ")
    ) {
      return
    }

    if (search(character) === null) return

    event.preventDefault()
    if (!("continuePropagation" in event)) event.stopPropagation()
  }

  return {
    typeSelectProps: {
      onKeyDownCapture,
      onKeyDown,
    },
    resetTypeSelect,
  }
}

function getStringForKey(key: string): string {
  // Multi-code-unit keys without an ASCII name are Unicode input, not named control keys.
  // https://www.w3.org/TR/uievents-key/
  if (key.length === 1 || !/^[A-Z]/i.test(key)) return key
  return ""
}
